import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import SEO from "../../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        const msg = typeof data?.detail === "string" ? data.detail : "Too many requests. Try again later.";
        setError(msg);
        return;
      }
      // Success path: always show the same generic confirmation regardless of whether
      // the email exists in our system — prevents user enumeration.
      setDone(true);
    } catch (_) {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Reset your password · Ridgehawk Admin" path="/admin/forgot-password" noindex />
      <div className="flex min-h-screen items-center justify-center bg-ridge-charcoal px-6 py-16" data-testid="forgot-password-page">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-3">
            <img src="/logo-icon.png" alt="" className="h-10 w-10 rounded bg-white p-0.5" />
            <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white">RIDGEHAWK · ADMIN</span>
          </div>

          <div className="rounded-md border border-white/10 bg-white p-7 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.5)] md:p-8">
            {done ? (
              <div data-testid="forgot-password-success">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-ridge-amber/15 text-ridge-amber">
                  <CheckCircle2 className="h-5 w-5" />
                </span>
                <h1 className="mt-4 font-display text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">
                  Check your inbox
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-ridge-muted">
                  If that email exists in our system, a reset link has been sent. The link
                  expires in 60 minutes and can be used once. Be sure to check spam if you
                  don't see it shortly.
                </p>
                <Link
                  to="/admin/login"
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.12em] text-ridge-amber transition-colors hover:text-ridge-amber-dark"
                  data-testid="forgot-back-to-login"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="forgot-password-form" noValidate>
                <h1 className="font-display text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">
                  Reset your password
                </h1>
                <p className="mt-2 text-sm text-ridge-muted">
                  Enter the email tied to your admin account and we'll send you a one-time reset link.
                </p>

                <div className="mt-6">
                  <label htmlFor="forgot-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal">
                    Email
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-password-email"
                    className="block w-full rounded-md border border-ridge-border bg-white px-3.5 py-3 text-sm focus:border-ridge-charcoal focus:outline-none focus:ring-2 focus:ring-ridge-charcoal/15 md:text-base"
                  />
                </div>

                {error ? (
                  <p
                    role="alert"
                    data-testid="forgot-password-error"
                    className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                  >
                    {error}
                  </p>
                ) : null}

                <button
                  type="submit"
                  className="btn-accent mt-6 w-full"
                  disabled={submitting || !email.trim()}
                  data-testid="forgot-password-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" /> Send reset link
                    </>
                  )}
                </button>

                <p className="mt-5 text-center text-sm">
                  <Link
                    to="/admin/login"
                    className="inline-flex items-center gap-1.5 font-semibold text-ridge-charcoal transition-colors hover:text-ridge-amber"
                    data-testid="forgot-back-to-login"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to sign in
                  </Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
